import { useEffect, useMemo, useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Link, useNavigate } from 'react-router-dom';
import { CardTile } from '../components/CardTile.tsx';
import { IconPlus, IconSearch } from '../components/icons.tsx';
import { SyncBadge } from '../components/SyncBadge.tsx';
import { db, listCards } from '../lib/db.ts';
import { cardsNear, getPosition, locationPermission, type NearbyCard } from '../lib/places.ts';
import { onSync } from '../lib/sync.ts';

/**
 * When the app was last checked against the remembered places. Module state, not component
 * state: coming back to the list from a card must not re-open that card, but reopening the app
 * after a while — the next shop — should check again.
 */
let lastCheck = 0;
const RECHECK_AFTER_MS = 10 * 60_000;

/** Exported for tests: lets a test start from "the app has just been opened". */
export function resetAutoOpen(): void {
  lastCheck = 0;
}

/**
 * Looks for remembered places near here, once per launch (and again after the app has been in
 * the background a while). Never blocks the list: the cards are on screen from the first frame,
 * and the nearby card takes over only if a position arrives while the user has not started
 * doing something else.
 */
function useNearby(): { nearby: NearbyCard[]; touched: React.MutableRefObject<boolean> } {
  const navigate = useNavigate();
  const [nearby, setNearby] = useState<NearbyCard[]>([]);
  const touched = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (Date.now() - lastCheck < RECHECK_AFTER_MS) return;
      const places = await db.places.filter((p) => !p.deleted).toArray();
      if (places.length === 0) return;
      // A refused permission stays refused; asking again on every launch would only nag.
      if ((await locationPermission()) === 'denied') return;
      lastCheck = Date.now();
      try {
        const here = await getPosition({ timeout: 8_000 });
        if (cancelled) return;
        const found = cardsNear(here, await listCards(), places);
        setNearby(found);
        if (found.length > 0 && !touched.current) {
          const [first, ...others] = found;
          navigate(`/card/${first!.card.id}`, {
            viewTransition: true,
            state: { nearby: true, others: others.map((n) => n.card.id) },
          });
        }
      } catch {
        // No position: the list is already on screen, which is the right fallback.
      }
    };

    void check();
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        touched.current = false;
        void check();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    // On a new phone the places arrive with the first sync, after the check above found none.
    // `check` is a no-op once it has run, so this only matters until it has.
    const offSync = onSync(() => void check());
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      offSync();
    };
  }, [navigate]);

  return { nearby, touched };
}

export default function Cards() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const cards = useLiveQuery(() => listCards(), [], undefined);
  const { nearby, touched } = useNearby();

  const visible = useMemo(() => {
    if (!cards) return undefined;
    const q = query.trim().toLowerCase();
    if (!q) return cards;
    return cards.filter((c) => c.name.toLowerCase().includes(q) || c.barcodeValue.toLowerCase().includes(q));
  }, [cards, query]);

  const open = (id: string) => navigate(`/card/${id}`, { viewTransition: true });
  const nearbyIds = new Set(nearby.map((n) => n.card.id));

  return (
    // Any touch means the user is choosing for themselves: a late position fix must not snatch
    // the screen away from them.
    <div onPointerDown={() => { touched.current = true; }} onKeyDown={() => { touched.current = true; }}>
      <header className="home-head">
        <h1 className="wordmark">Klarnus <SyncBadge /></h1>
        <Link className="pill" to="/manage">Gestisci</Link>
      </header>

      <main className="content">
        <div className="stack">
          {cards && cards.length > 6 && (
            <label className="search">
              <IconSearch size={20} />
              <input
                type="search" inputMode="search" placeholder={`Cerca tra ${cards.length} carte`}
                value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Cerca una carta"
              />
            </label>
          )}

          {visible === undefined && <p className="muted">Apro il portafoglio…</p>}

          {cards?.length === 0 && (
            <div className="empty">
              <div className="fan" aria-hidden="true"><span /><span /><span /></div>
              <h2>Il portafoglio è vuoto</h2>
              <p>Tocca + e inquadra il codice a barre della tua prima carta.</p>
            </div>
          )}

          {nearby.length > 0 && !query && (
            <section className="nearby stack" aria-label="Qui vicino">
              <h2 className="section-title">Qui vicino</h2>
              <div className="wallet-grid">
                {nearby.map(({ card }) => (
                  <CardTile key={card.id} card={card} badge="Sei in un posto dove la usi" onClick={() => open(card.id)} />
                ))}
              </div>
            </section>
          )}

          {visible && visible.length === 0 && (cards?.length ?? 0) > 0 && (
            <p className="muted">Nessuna carta si chiama “{query}”.</p>
          )}

          {visible && visible.length > 0 && (
            <div className="wallet-grid">
              {visible.filter((c) => query || !nearbyIds.has(c.id)).map((card) => (
                <CardTile key={card.id} card={card} onClick={() => open(card.id)} />
              ))}
            </div>
          )}
        </div>
      </main>

      <button className="fab-add" onClick={() => navigate('/add')} aria-label="Aggiungi una carta">
        <IconPlus size={30} strokeWidth={2.4} />
      </button>
    </div>
  );
}
